import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ShieldAlert, ShieldCheck, AlertTriangle, MemoryStick, HardDrive, XCircle, CheckCircle2 } from 'lucide-react';

export const HealthBadges = ({ health, compact = false }) => {
  if (!health) return null;

  const { issues_count } = health;

  if (issues_count === 0) {
    return (
      <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20 text-xs" data-testid="health-badge-ok">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {compact ? 'OK' : 'All Clear'}
      </Badge>
    );
  }

  const badges = [];

  if (health.cert_expired) {
    badges.push({
      key: 'cert_expired',
      label: 'Cert Expired',
      icon: ShieldAlert,
      className: 'bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20',
    });
  }

  if (health.cert_expiring_soon) {
    badges.push({
      key: 'cert_expiring',
      label: 'Cert Expiring',
      icon: ShieldAlert,
      className: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
    });
  }

  if (!health.cert_expiring_soon && health.cert_expiring_soon_autorenew) {
    badges.push({
      key: 'cert_expiring_autorenew',
      label: 'Cert Expiring (Auto)',
      icon: ShieldCheck,
      className: 'bg-white/5 text-[#a1a1aa] border-white/10',
    });
  }

  if (health.failed_units) {
    badges.push({
      key: 'failed_units',
      label: 'Failed Units',
      icon: XCircle,
      className: 'bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20',
    });
  }

  if (health.high_memory) {
    badges.push({
      key: 'high_memory',
      label: `Memory ${health.memory_pct}%`,
      icon: MemoryStick,
      className: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
    });
  }

  if (health.high_disk) {
    badges.push({
      key: 'high_disk',
      label: `Disk ${health.disk_pct}%`,
      icon: HardDrive,
      className: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
    });
  }

  if (compact && badges.length > 2) {
    const shown = badges.slice(0, 2);
    return (
      <TooltipProvider>
        <div className="flex flex-wrap gap-1">
          {shown.map(b => {
            const Icon = b.icon;
            return (
              <Badge key={b.key} data-testid={`health-badge-${b.key}`} className={`${b.className} text-[10px]`}>
                <Icon className="w-3 h-3 mr-1" />
                {b.label}
              </Badge>
            );
          })}
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="bg-white/5 text-[#a1a1aa] border-white/10 text-[10px] cursor-help">
                +{badges.length - 2}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="bg-[#121214] border-white/10 text-white">
              {badges.slice(2).map(b => <div key={b.key}>{b.label}</div>)}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {badges.map(b => {
        const Icon = b.icon;
        return (
          <Badge key={b.key} data-testid={`health-badge-${b.key}`} className={`${b.className} text-[10px]`}>
            <Icon className="w-3 h-3 mr-1" />
            {b.label}
          </Badge>
        );
      })}
    </div>
  );
};
